import { Component, OnInit, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { AssistantWindowHost } from '@core/services/assistant/assistant-window-host';
import { AssistantService } from '@core/services/assistant/assistant.service';
import { DataspaceRoutingService } from '@core/services/dataspace-routing/dataspace-routing.service';
import { ICONS } from '@core/services/icons/icons.service';
import { GlobalShortcutsService } from '@core/services/shortcuts.service';
import { selectDataspaceModel } from '@core/store/dataspace/dataspace.selectors';

import { BreadcrumbComponent } from '@shared/breadcrumb/breadcrumb.component';
import { DataspaceSelectorComponent } from '@shared/components/dataspace-selector/dataspace-selector.component';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { Shortcut } from '@shared/utils/shortcut';

import { PlanPanelService } from '@features/components/plan/plan-panel/plan-panel.service';

@Component({
  selector: 'app-top-bar',
  imports: [BreadcrumbComponent, DataspaceSelectorComponent, TooltipComponent],
  templateUrl: './top-bar.component.html',
  styles: ``
})
export class TopBarComponent implements OnInit {
  burgerIcon = ICONS.list;
  isSidebarOpen = input.required<boolean>();
  showDataspaceDropdown = input<boolean>(true);
  openSearchPopup = output();
  searchShortcut = new Shortcut('Meta./', () => {
    this.openSearchPopup.emit();
  });
  toggleSidebar = output();

  private readonly dataspaceRoutingService = inject(DataspaceRoutingService);
  private readonly globalShortcutsService = inject(GlobalShortcutsService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);

  protected readonly assistant = inject(AssistantService);
  private readonly assistantWindow = inject(AssistantWindowHost);
  protected readonly poppedOut = computed(
    () => this.assistantWindow.mode() === 'popped-out'
  );
  protected readonly ICONS = ICONS;

  protected readonly planPanel = inject(PlanPanelService);

  private currentDataspace = this.store.selectSignal(selectDataspaceModel);

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected currentDataspaceId = computed<number | null>(
    () => this.currentDataspace()?.id ?? null
  );

  protected readonly assistantToggleLabel = computed(() => {
    this.currentLang();
    if (this.poppedOut()) return this.translate.instant('assistant.popped_out');
    return this.translate.instant(
      this.assistant.panelOpen() ? 'assistant.toggle_close' : 'assistant.toggle_open'
    );
  });

  protected readonly planPanelToggleLabel = computed(() => {
    this.currentLang();
    return this.translate.instant('features.plan.panel.toggle');
  });

  protected onAssistantToggle(): void {
    if (this.poppedOut()) this.assistantWindow.focusChild();
    else this.assistant.togglePanel();
  }

  protected dataspaceTooltip = computed<string>(() => {
    this.currentLang();
    return this.translate.instant('topbar.dataspace.change');
  });

  protected dataspacePlaceholder = computed<string>(() => {
    this.currentLang();
    const current = this.currentDataspace();
    return current?.name ?? this.translate.instant('topbar.dataspace.select');
  });

  ngOnInit(): void {
    this.globalShortcutsService.register(this.searchShortcut);
  }

  protected onDataspaceChange(value: number | null): void {
    if (value === null) return;
    if (this.currentDataspace()?.id !== value) {
      this.dataspaceRoutingService.switchToDataspace(value);
    }
  }
}
